// src/utils/DOMUtils.ts

type TextOrPlaceholder = {
  node: Text | HTMLElement;
  length: number;
  isPlaceholder?: boolean;
};

export function getTextNodesUnder(
  root: Node,
  options?: { skipSelector?: string }
) {
  // Usar el selector proporcionado o el predeterminado para fórmulas
  const skipSelector =
    options?.skipSelector ||
    ".katex, .__se__katex, math, .katex-mathml, .katex-html";
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ALL, null);
  const result: TextOrPlaceholder[] = [];
  let n: Node | null = walker.currentNode;

  n = walker.nextNode();
  while (n) {
    if (
      n.nodeType === Node.ELEMENT_NODE &&
      skipSelector &&
      (n as Element).matches &&
      (n as Element).matches(skipSelector)
    ) {
      const el = n as HTMLElement;
      const txt = el.textContent || "";
      result.push({ node: el, length: txt.length, isPlaceholder: true });
      const skipTarget = n;
      const skipDepth = getNodeDepth(skipTarget);
      n = walker.nextNode();
      while (n && getNodeDepth(n) > skipDepth) {
        n = walker.nextNode();
      }
      continue;
    }

    if (n.nodeType === Node.TEXT_NODE) {
      const t = n as Text;
      const txt = t.textContent || "";
      if (txt.length > 0) {
        result.push({ node: t, length: txt.length, isPlaceholder: false });
      }
    }
    n = walker.nextNode();
  }

  return result;
}

function getNodeDepth(node: Node | null) {
  let d = 0;
  while (node && node.parentNode) {
    d++;
    node = node.parentNode;
  }
  return d;
}

export function getRangeIndexesRelativeToContainer(
  container: HTMLElement,
  range: Range
) {
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null
  );
  const textNodes: Text[] = [];
  let n = walker.nextNode();
  while (n) {
    if (n.nodeType === Node.TEXT_NODE) {
      const t = n as Text;
      if (t.textContent && t.textContent.length > 0) textNodes.push(t);
    }
    n = walker.nextNode();
  }

  const nodeStarts: number[] = new Array(textNodes.length);
  let acc = 0;
  for (let i = 0; i < textNodes.length; i++) {
    nodeStarts[i] = acc;
    acc += textNodes[i].textContent?.length || 0;
  }

  function indexOfTextNode(node: Node | null) {
    if (!node) return -1;
    for (let i = 0; i < textNodes.length; i++) {
      if (textNodes[i] === node) return i;
    }
    return -1;
  }

  let start = -1;
  let end = -1;

  if (range.startContainer.nodeType === Node.TEXT_NODE) {
    const idx = indexOfTextNode(range.startContainer);
    if (idx !== -1) {
      start = nodeStarts[idx] + (range.startOffset || 0);
    }
  }

  if (range.endContainer.nodeType === Node.TEXT_NODE) {
    const idx = indexOfTextNode(range.endContainer);
    if (idx !== -1) {
      end = nodeStarts[idx] + (range.endOffset || 0);
    }
  }

  function firstDescendantTextNodeIndex(el: Node | null) {
    if (!el) return -1;
    for (let i = 0; i < textNodes.length; i++) {
      if (el.contains(textNodes[i])) return i;
    }
    return -1;
  }

  if (start === -1) {
    const idx = firstDescendantTextNodeIndex(range.startContainer);
    if (idx !== -1) start = nodeStarts[idx];
  }

  if (end === -1) {
    const idx = firstDescendantTextNodeIndex(range.endContainer);
    if (idx !== -1)
      end = nodeStarts[idx] + (textNodes[idx].textContent?.length || 0);
  }

  if (start === -1 || end === -1) {
    const selText = range.toString();
    const full = container.textContent || "";
    if (selText.length === 0) return { start: 0, end: 0 };

    const occurrences: number[] = [];
    let pos = 0;
    while (true) {
      const found = full.indexOf(selText, pos);
      if (found === -1) break;
      occurrences.push(found);
      pos = found + selText.length;
    }

    if (occurrences.length === 0) {
      return { start: 0, end: 0 };
    } else if (occurrences.length === 1) {
      start = occurrences[0];
      end = start + selText.length;
    } else {
      const approxNodeIndex =
        range.startContainer.nodeType === Node.TEXT_NODE
          ? indexOfTextNode(range.startContainer)
          : firstDescendantTextNodeIndex(range.startContainer);

      if (approxNodeIndex === -1) {
        start = occurrences[0];
        end = start + selText.length;
      } else {
        const occurrenceNodeIndexes = occurrences.map((occ) => {
          let foundIdx = -1;
          for (let i = 0; i < nodeStarts.length; i++) {
            const s = nodeStarts[i];
            const l = textNodes[i].textContent?.length || 0;
            if (occ >= s && occ < s + l) {
              foundIdx = i;
              break;
            }
          }
          return foundIdx;
        });

        let best = 0;
        let bestDist = Infinity;
        for (let i = 0; i < occurrenceNodeIndexes.length; i++) {
          const ni = occurrenceNodeIndexes[i];
          if (ni === -1) continue;
          const dist = Math.abs(ni - approxNodeIndex);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        }
        const chosen = bestDist === Infinity ? 0 : best;
        start = occurrences[chosen];
        end = start + selText.length;
      }
    }
  }

  start = Math.max(0, start);
  end = Math.max(0, end);

  return { start, end };
}

export function removeMarks(container: HTMLElement) {
  if (!container) return;

  // PRIMERO RESTAURAR ENLACES ANTES DE ELIMINAR MARKS
  const linkContainers = Array.from(
    container.querySelectorAll("span[data-original-link]")
  );
  linkContainers.forEach((linkContainer) => {
    const originalLink = document.createElement("a");
    originalLink.href = linkContainer.getAttribute("data-original-href") || "#";
    originalLink.textContent =
      linkContainer.getAttribute("data-original-text") || "";

    // Restaurar estilos originales si estaban presentes
    const originalColor = linkContainer.getAttribute("data-original-color");
    const originalDecoration = linkContainer.getAttribute(
      "data-original-decoration"
    );
    if (originalColor) originalLink.style.color = originalColor;
    if (originalDecoration)
      originalLink.style.textDecoration = originalDecoration;

    linkContainer.parentNode?.replaceChild(originalLink, linkContainer);
  });

  // Elimina únicamente los <mark> de los resaltados
  const marks = Array.from(container.querySelectorAll("mark[data-hl-id]"));
  marks.forEach((m) => {
    const parent = m.parentNode!;
    while (m.firstChild) parent.insertBefore(m.firstChild, m);
    parent.removeChild(m);
  });

  // Luego limpia los íconos de nota que queden huérfanos
  const noteIcons = Array.from(
    container.querySelectorAll(".highlight-note-icon")
  );
  noteIcons.forEach((icon) => icon.remove());

  // Limpiar íconos de enlace
  const linkIcons = Array.from(
    container.querySelectorAll(".highlight-link-icon")
  );
  linkIcons.forEach((icon) => icon.remove());
}

/**
 * Combina y divide correctamente los rangos de resaltado superpuestos
 */
function mergeOverlappingHighlights(
  highlights: Array<{
    id: string;
    startIndex: number;
    endIndex: number;
    color: string;
    note?: string;
  }>
) {
  if (!highlights || highlights.length === 0) return [];

  const sorted = [...highlights].sort((a, b) => a.startIndex - b.startIndex);
  const merged: typeof highlights = [];

  for (const hl of sorted) {
    const last = merged[merged.length - 1];

    if (!last || hl.startIndex >= last.endIndex) {
      merged.push({ ...hl });
    } else {
      if (hl.startIndex > last.startIndex) {
        merged.push({ ...last, endIndex: hl.startIndex });
      }
      merged.push({ ...hl });
      if (hl.endIndex < last.endIndex) {
        merged.push({ ...last, startIndex: hl.endIndex });
      }
    }
  }

  return merged;
}

/**
 * - Trata bloques .katex / math / .__se__katex como atómicos.
 * - Combina rangos superpuestos y preserva los anteriores.
 * - Convierte enlaces en texto normal con ícono azul permanente.
 */
export function applyHighlightsToContainer(
  container: HTMLElement,
  highlights: Array<{
    id: string;
    startIndex: number;
    endIndex: number;
    color: string;
    note?: string;
  }>
) {
  if (!container) return;

  removeMarks(container);
  if (!highlights || highlights.length === 0) return;

  const mergedHighlights = mergeOverlappingHighlights(highlights);

  const KATEX_SELECTOR = ".katex, .__se__katex, math";
  const chunks = getTextNodesUnder(container, { skipSelector: KATEX_SELECTOR });

  const nodeStarts: number[] = new Array(chunks.length);
  const nodes: (Text | HTMLElement)[] = new Array(chunks.length);
  let acc = 0;
  for (let i = 0; i < chunks.length; i++) {
    nodeStarts[i] = acc;
    nodes[i] = chunks[i].node;
    acc += chunks[i].length;
  }

  function getNodeLength(n: Text | HTMLElement) {
    if (!n) return 0;
    if (n.nodeType === Node.TEXT_NODE)
      return (n as Text).textContent?.length || 0;
    return (n as HTMLElement).textContent?.length || 0;
  }

  function findNodeIndexForPos(pos: number) {
    let lo = 0;
    let hi = nodeStarts.length - 1;
    if (nodeStarts.length === 0) return -1;
    if (pos < 0) return 0;
    if (pos >= nodeStarts[hi] + getNodeLength(nodes[hi])) return hi;

    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      const start = nodeStarts[mid];
      const end = start + getNodeLength(nodes[mid]);
      if (pos >= start && pos < end) return mid;
      if (pos < start) hi = mid - 1;
      else lo = mid + 1;
    }
    return Math.max(0, Math.min(nodeStarts.length - 1, lo));
  }

  // FUNCIÓN PARA CREAR ÍCONO DE ENLACE (MISMAS PROPORCIONES QUE NOTAS)
  function createLinkIcon(linkUrl: string | null) {
    const linkIconWrapper = document.createElement("span");
    linkIconWrapper.className = "btn__icon highlight-link-icon";
    linkIconWrapper.style.color = "white";
    linkIconWrapper.style.verticalAlign = "middle";
    linkIconWrapper.style.display = "inline-block";
    linkIconWrapper.style.padding = "5px 0px 5px 7px";
    linkIconWrapper.style.marginTop = "-3px";
    linkIconWrapper.style.cursor = "pointer";
    linkIconWrapper.title = `Enlace: ${linkUrl}`;

    const linkIcon = document.createElement("i");
    linkIcon.className = "icon icon-vpro-new-win";
    linkIcon.style.display = "flex";
    linkIcon.style.alignItems = "center";
    linkIcon.style.justifyContent = "center";
    linkIconWrapper.appendChild(linkIcon);

    // HACER QUE EL ÍCONO ABRA EL ENLACE EN NUEVA PESTAÑA
    linkIconWrapper.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (linkUrl) {
        window.open(linkUrl, "_blank");
      }
    });

    return linkIconWrapper;
  }

  // FUNCIÓN PARA CREAR CONTENEDOR DE ENLACE CON ESTILOS CONSISTENTES
  function createLinkContainer(linkElement: HTMLAnchorElement): HTMLElement {
    const linkUrl = linkElement.getAttribute("href");
    const linkText = linkElement.textContent;
    const computedStyle = window.getComputedStyle(linkElement);

    // CREAR SPAN CON DATOS PARA PODER RESTAURAR DESPUÉS
    const linkContainer = document.createElement("span");
    linkContainer.setAttribute("data-original-link", "true");
    linkContainer.setAttribute("data-original-href", linkUrl || "");
    linkContainer.setAttribute("data-original-text", linkText || "");
    linkContainer.setAttribute("data-original-color", computedStyle.color);
    linkContainer.setAttribute(
      "data-original-decoration",
      computedStyle.textDecoration
    );

    linkContainer.style.color = "inherit";
    linkContainer.style.textDecoration = "underline";
    linkContainer.style.fontWeight = "bold"; // CONSISTENCIA CON OTROS RESALTADOS
    linkContainer.style.cursor = "default";
    linkContainer.style.display = "inline";

    // AGREGAR EL TEXTO DEL ENLACE
    const textNode = document.createTextNode(linkText || "");
    linkContainer.appendChild(textNode);

    // AGREGAR ÍCONO AZUL (SIEMPRE, NO DEPENDE DE NOTAS)
    const linkIconWrapper = createLinkIcon(linkUrl);
    linkContainer.appendChild(linkIconWrapper);

    return linkContainer;
  }

  // FUNCIÓN PARA PROCESAR ELEMENTOS CON ENLACES
  function processLinksInElement(element: HTMLElement): HTMLElement {
    const clone = element.cloneNode(true) as HTMLElement;

    // Encontrar y procesar todos los enlaces dentro del clon
    const links = clone.querySelectorAll("a[href]");
    links.forEach((link) => {
      // CREAR CONTENEDOR CON ESTILOS CONSISTENTES
      const linkContainer = createLinkContainer(link as HTMLAnchorElement);

      // REEMPLAZAR COMPLETAMENTE EL ENLACE POR EL NUEVO SPAN
      link.parentNode?.replaceChild(linkContainer, link);
    });

    // Si el elemento mismo es un enlace, procesarlo
    if (clone.tagName === "A") {
      return createLinkContainer(clone as HTMLAnchorElement);
    }

    return clone;
  }

  const sorted = [...mergedHighlights].sort(
    (a, b) => b.startIndex - a.startIndex
  );

  for (const hl of sorted) {
    const startIdx = Math.max(0, hl.startIndex);
    const endIdx = Math.max(0, hl.endIndex);
    if (startIdx >= endIdx) continue;

    const nodeI = findNodeIndexForPos(startIdx);
    const nodeJ = findNodeIndexForPos(Math.max(endIdx - 1, startIdx));
    if (nodeI === -1 || nodeJ === -1) continue;

    for (let ni = nodeJ; ni >= nodeI; ni--) {
      const n = nodes[ni];
      const nodeStart = nodeStarts[ni];
      const nodeLen = getNodeLength(n);
      const nodeEnd = nodeStart + nodeLen;

      if (nodeEnd <= startIdx || nodeStart >= endIdx) continue;

      const localStart = Math.max(0, startIdx - nodeStart);
      const localEnd = Math.min(nodeLen, endIdx - nodeStart);
      const segmentEndsAtHighlightEnd = nodeStart + localEnd === endIdx;

      if (n.nodeType === Node.ELEMENT_NODE) {
        const el = n as HTMLElement;

        const mark = document.createElement("mark");
        mark.dataset.hlId = hl.id;
        mark.style.background = hl.color;
        mark.style.cursor = "pointer";
        mark.style.padding = "2.5px 2px";
        if (hl.color === "#999FD9" || hl.color === "#E0314E") {
          mark.style.color = "#fff";
        }
        mark.style.fontWeight = "bold";

        // VERIFICAR SI ES UN CONTENEDOR DE ENLACE PREVIAMENTE PROCESADO
        const isAlreadyProcessedLink = el.hasAttribute("data-original-link");

        if (isAlreadyProcessedLink) {
          // SI YA ES UN ENLACE PROCESADO, SOLO AGREGARLO AL MARK
          mark.appendChild(el.cloneNode(true));
        } else {
          // VERIFICAR SI EL ELEMENTO ES UN ENLACE O CONTIENE ENLACES
          const containsLinks =
            el.querySelectorAll("a[href]").length > 0 || el.tagName === "A";

          if (containsLinks) {
            // USAR LA FUNCIÓN ESPECIAL PARA PROCESAR ENLACES
            const processedElement = processLinksInElement(el);
            mark.appendChild(processedElement);
          } else {
            // COMPORTAMIENTO NORMAL PARA ELEMENTOS SIN ENLACES
            const clone = el.cloneNode(true);
            mark.appendChild(clone);
          }
        }

        let wrapper: HTMLElement = mark;
        // EL ÍCONO DE ENLACE Y EL ÍCONO DE NOTA SON INDEPENDIENTES
        if (hl.note) {
          wrapper = document.createElement("span");
          wrapper.style.display = "inline";
          wrapper.style.whiteSpace = "inherit";
          wrapper.appendChild(mark);

          if (segmentEndsAtHighlightEnd) {
            // Ícono de nota (SOLO SI HAY NOTA)
            const iconWrapper = document.createElement("span");
            iconWrapper.className = "btn__icon highlight-note-icon";
            iconWrapper.style.backgroundColor = "black";
            iconWrapper.style.color = "white";
            iconWrapper.style.verticalAlign = "middle";
            iconWrapper.style.display = "inline-block";
            iconWrapper.style.padding = "1px 3px 1px 3px";
            iconWrapper.style.marginTop = "-3px";
            iconWrapper.style.cursor = "pointer";
            iconWrapper.style.fontSize = "20px"
            iconWrapper.dataset.hlId = hl.id;

            const icon = document.createElement("i");
            icon.className = "icon icon-vpro-comments";
            icon.style.display = "flex";
            icon.style.alignItems = "center";
            icon.style.justifyContent = "center";
            iconWrapper.appendChild(icon);

            wrapper.appendChild(iconWrapper);
          }
        }

        el.parentNode?.replaceChild(wrapper, el);
      } else {
        const textNode = n as Text;
        if (!textNode.textContent || textNode.textContent.length === 0)
          continue;

        const after = textNode.splitText(localEnd);
        const middle = textNode.splitText(localStart);

        // VERIFICAR SI EL TEXT NODE ESTÁ DENTRO DE UN ENLACE O CONTENEDOR PROCESADO
        const parentElement = textNode.parentElement;
        const isInsideLink =
          parentElement?.tagName === "A" || parentElement?.closest("a[href]");
        const isInsideProcessedLink =
          parentElement?.hasAttribute("data-original-link") ||
          parentElement?.closest("[data-original-link]");

        if (isInsideLink && !isInsideProcessedLink) {
          // PROCESAR ENLACE NUEVO
          const linkElement =
            parentElement?.tagName === "A"
              ? parentElement
              : parentElement?.closest("a[href]");

          if (linkElement) {
            const mark = document.createElement("mark");
            mark.dataset.hlId = hl.id;
            mark.style.background = hl.color;
            mark.style.cursor = "pointer";
            mark.style.padding = "2.5px 2px";
            if (hl.color === "#999FD9" || hl.color === "#E0314E") {
              mark.style.color = "#fff";
            }
            mark.style.fontWeight = "bold";

            // PROCESAR EL ENLACE COMPLETO CON ÍCONO PERMANENTE
            const linkContainer = createLinkContainer(
              linkElement as HTMLAnchorElement
            );
            mark.appendChild(linkContainer);

            let wrapper: HTMLElement = mark;
            // EL ÍCONO DE ENLACE ES INDEPENDIENTE DEL ÍCONO DE NOTA
            if (hl.note) {
              wrapper = document.createElement("span");
              wrapper.style.display = "inline";
              wrapper.style.whiteSpace = "inherit";
              wrapper.appendChild(mark);

              if (segmentEndsAtHighlightEnd) {
                const iconWrapper = document.createElement("span");
                iconWrapper.className = "btn__icon highlight-note-icon";
                iconWrapper.style.fontSize = "20px"
                iconWrapper.style.backgroundColor = "black";
                iconWrapper.style.color = "white";
                iconWrapper.style.verticalAlign = "middle";
                iconWrapper.style.display = "inline-block";
                iconWrapper.style.padding = "1px 3px 1px 3px";
                iconWrapper.style.marginTop = "-3px";
                iconWrapper.style.cursor = "pointer";
                iconWrapper.dataset.hlId = hl.id;

                const icon = document.createElement("i");
                icon.className = "icon icon-vpro-comments";
                icon.style.display = "flex";
                icon.style.alignItems = "center";
                icon.style.justifyContent = "center";
                iconWrapper.appendChild(icon);

                wrapper.appendChild(iconWrapper);
              }
            }

            // REEMPLAZAR EL ENLACE COMPLETO POR EL MARK
            linkElement.parentNode?.replaceChild(wrapper, linkElement);
          } else {
            // Fallback: comportamiento normal si no se puede encontrar el enlace
            const mark = document.createElement("mark");
            mark.dataset.hlId = hl.id;
            mark.style.background = hl.color;
            if (hl.color === "#999FD9" || hl.color === "#E0314E") {
              mark.style.color = "#fff";
            }
            mark.style.cursor = "pointer";
            mark.style.padding = "2.5px 2px";
            mark.style.fontWeight = "bold";
            mark.appendChild(middle.cloneNode(true));

            let wrapper: HTMLElement = mark;
            if (hl.note) {
              wrapper = document.createElement("span");
              wrapper.style.display = "inline";
              wrapper.style.whiteSpace = "inherit";
              wrapper.appendChild(mark);

              if (segmentEndsAtHighlightEnd) {
                const iconWrapper = document.createElement("span");
                iconWrapper.className = "btn__icon highlight-note-icon";
                iconWrapper.style.fontSize = "20px"
                iconWrapper.style.backgroundColor = "black";
                iconWrapper.style.color = "white";
                iconWrapper.style.verticalAlign = "middle";
                iconWrapper.style.display = "inline-block";
                iconWrapper.style.padding = "1px 3px 1px 3px";
                iconWrapper.style.marginTop = "-3px";
                iconWrapper.style.cursor = "pointer";
                iconWrapper.dataset.hlId = hl.id;

                const icon = document.createElement("i");
                icon.className = "icon icon-vpro-comments";
                icon.style.display = "flex";
                icon.style.alignItems = "center";
                icon.style.justifyContent = "center";
                iconWrapper.appendChild(icon);

                wrapper.appendChild(iconWrapper);
              }
            }

            middle.parentNode?.replaceChild(wrapper, middle);
          }
        } else if (isInsideProcessedLink) {
          // SI YA ES UN ENLACE PROCESADO, SOLO AGREGAR AL MARK EXISTENTE
          const processedLinkContainer = parentElement?.hasAttribute(
            "data-original-link"
          )
            ? parentElement
            : parentElement?.closest("[data-original-link]");

          if (processedLinkContainer) {
            const mark = document.createElement("mark");
            mark.dataset.hlId = hl.id;
            mark.style.background = hl.color;
            mark.style.cursor = "pointer";
            mark.style.padding = "2.5px 2px";
            if (hl.color === "#999FD9" || hl.color === "#E0314E") {
              mark.style.color = "#fff";
            }
            mark.style.fontWeight = "bold";
            mark.appendChild(processedLinkContainer.cloneNode(true));

            let wrapper: HTMLElement = mark;
            if (hl.note) {
              wrapper = document.createElement("span");
              wrapper.style.display = "inline";
              wrapper.style.whiteSpace = "inherit";
              wrapper.appendChild(mark);

              if (segmentEndsAtHighlightEnd) {
                const iconWrapper = document.createElement("span");
                iconWrapper.className = "btn__icon highlight-note-icon";
                iconWrapper.style.fontSize = "20px"
                iconWrapper.style.backgroundColor = "black";
                iconWrapper.style.color = "white";
                iconWrapper.style.verticalAlign = "middle";
                iconWrapper.style.display = "inline-block";
                iconWrapper.style.padding = "1px 3px 1px 3px";
                iconWrapper.style.marginTop = "-3px";
                iconWrapper.style.cursor = "pointer";
                iconWrapper.dataset.hlId = hl.id;

                const icon = document.createElement("i");
                icon.className = "icon icon-vpro-comments";
                icon.style.display = "flex";
                icon.style.alignItems = "center";
                icon.style.justifyContent = "center";
                iconWrapper.appendChild(icon);

                wrapper.appendChild(iconWrapper);
              }
            }

            processedLinkContainer.parentNode?.replaceChild(
              wrapper,
              processedLinkContainer
            );
          }
        } else {
          // COMPORTAMIENTO NORMAL PARA TEXT NODES SIN ENLACES
          const mark = document.createElement("mark");
          mark.dataset.hlId = hl.id;
          mark.style.background = hl.color;
          if (hl.color === "#999FD9" || hl.color === "#E0314E") {
            mark.style.color = "#fff";
          }
          mark.style.cursor = "pointer";
          mark.style.padding = "2.5px 2px";
          mark.style.fontWeight = "bold";
          mark.appendChild(middle.cloneNode(true));

          let wrapper: HTMLElement = mark;
          if (hl.note) {
            wrapper = document.createElement("span");
            wrapper.style.display = "inline";
            wrapper.style.whiteSpace = "inherit";
            wrapper.appendChild(mark);

            if (segmentEndsAtHighlightEnd) {
              const iconWrapper = document.createElement("span");
              iconWrapper.className = "btn__icon highlight-note-icon";
              iconWrapper.style.fontSize = "20px"
              iconWrapper.style.backgroundColor = "black";
              iconWrapper.style.color = "white";
              iconWrapper.style.verticalAlign = "middle";
              iconWrapper.style.display = "inline-block";
              iconWrapper.style.padding = "1px 3px 1px 3px";
              iconWrapper.style.marginTop = "-3px";
              iconWrapper.style.cursor = "pointer";
              iconWrapper.dataset.hlId = hl.id;

              const icon = document.createElement("i");
              icon.className = "icon icon-vpro-comments";
              icon.style.display = "flex";
              icon.style.alignItems = "center";
              icon.style.justifyContent = "center";
              iconWrapper.appendChild(icon);

              wrapper.appendChild(iconWrapper);
            }
          }

          middle.parentNode?.replaceChild(wrapper, middle);
        }
      }
    }
  }
}
